import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, downloadAuthed } from "@/lib/api";
import { usePay } from "@/context/CompanyContext";
import { periodStartISO, periodDays, periodWord } from "@/lib/payPeriod";

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const hrs = (v) => (v ? v.toFixed(2) : "–");
const OT = "text-orange-600";

const Row = ({ r }) => (
  <tr className="border-b border-border last:border-0" data-testid={`hours-row-${r.user_id}`}>
    <td className="px-4 py-2.5 font-medium sticky left-0 bg-card whitespace-nowrap">{r.name}</td>
    {r.hours.map((v, i) => (
      <td key={i} className="px-3 py-2.5 text-right font-mono" data-testid={`hours-cell-${r.user_id}-${i}`}>
        {hrs(v)}{r.overtime[i] > 0 && <span className={`block text-[11px] ${OT}`}>+{r.overtime[i].toFixed(2)} o/t</span>}
      </td>
    ))}
    <td className="px-3 py-2.5 text-right font-mono font-semibold" data-testid={`hours-total-${r.user_id}`}>{r.total.toFixed(2)}</td>
    <td className={`px-4 py-2.5 text-right font-mono ${OT}`} data-testid={`hours-ot-${r.user_id}`}>{r.total_ot.toFixed(2)}</td>
  </tr>
);

const HoursTable = ({ d }) => (
  <div className="bg-card border border-border rounded-2xl overflow-x-auto" data-testid="hours-table">
    <table className="w-full text-sm min-w-[640px]">
      <thead>
        <tr className="border-b border-border text-xs text-muted-foreground">
          <th className="text-left font-semibold px-4 py-3 sticky left-0 bg-card">Worker</th>
          {d.periods.map((p) => <th key={p.start} className="font-semibold px-3 py-3 text-right whitespace-nowrap">{p.label}</th>)}
          <th className="font-semibold px-3 py-3 text-right">Total</th>
          <th className="font-semibold px-4 py-3 text-right">O/T</th>
        </tr>
      </thead>
      <tbody>{d.rows.map((r) => <Row key={r.user_id} r={r} />)}</tbody>
      <tfoot>
        <tr className="border-t-2 border-border font-semibold bg-secondary">
          <td className="px-4 py-3 sticky left-0 bg-secondary">Total</td>
          {d.totals.map((v, i) => <td key={i} className="px-3 py-3 text-right font-mono" data-testid={`hours-period-total-${i}`}>{hrs(v)}</td>)}
          <td className="px-3 py-3 text-right font-mono" data-testid="hours-grand-total">{d.total.toFixed(2)}</td>
          <td className={`px-4 py-3 text-right font-mono ${OT}`} data-testid="hours-grand-ot">{d.total_ot.toFixed(2)}</td>
        </tr>
      </tfoot>
    </table>
  </div>
);

// Every worker's hours across several pay periods, with CSV and PDF downloads.
export const HoursTab = () => {
  const pay = usePay();
  const word = periodWord(pay);
  const current = periodStartISO(new Date(), pay);
  const [n, setN] = useState("6");
  const [last, setLast] = useState(current);
  const [d, setD] = useState(null);
  useEffect(() => { setLast(current); }, [current]);
  useEffect(() => {
    let live = true;
    setD(null);
    api.hoursReport({ periods: n, last_start: last }).then((r) => live && setD(r))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load hours"));
    return () => { live = false; };
  }, [n, last]);

  const q = `periods=${n}&last_start=${last}`;
  const dl = (ext) => downloadAuthed(`/coordinator/hours-report.${ext}?${q}`, `hours-report-${last}.${ext}`).catch((e) => toast.error(e.message));
  const step = (k) => setLast(addDays(last, k * periodDays(pay)));

  return (
    <div className="fade-up" data-testid="hours-tab">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold">Hours</h2>
          <p className="text-sm text-muted-foreground">Each worker&apos;s hours per pay {word}, side by side.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => dl("csv")} disabled={!d?.rows.length} className="rounded-full" data-testid="hours-csv-btn"><Download className="w-4 h-4 mr-2" /> CSV</Button>
          <Button variant="outline" onClick={() => dl("pdf")} disabled={!d} className="rounded-full" data-testid="hours-pdf-btn"><FileText className="w-4 h-4 mr-2" /> PDF</Button>
        </div>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3" data-testid="hours-controls">
        <Select value={n} onValueChange={setN}>
          <SelectTrigger className="h-9 w-40" data-testid="hours-periods-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["4", "6", "8", "13"].map((k) => <SelectItem key={k} value={k}>Last {k} pay {word}s</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-2">up to the {word} starting</span>
        <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" onClick={() => step(-1)} aria-label="Earlier" data-testid="hours-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
        <Input type="date" value={last} onChange={(e) => e.target.value && setLast(periodStartISO(e.target.value, pay))} className="h-9 w-auto" data-testid="hours-last-input" />
        <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" onClick={() => step(1)} disabled={last >= current} aria-label="Later" data-testid="hours-next-btn"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      {!d ? <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
        : d.rows.length === 0 ? <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground" data-testid="hours-empty">No shifts in these pay {word}s.</div>
          : <HoursTable d={d} />}
      {d && <p className="text-xs text-muted-foreground mt-2" data-testid="hours-ot-note">Hours are rounded to the nearest 15 minutes per shift. {d.overtime_note} Change the overtime rule in Settings → Edit payroll settings.</p>}
    </div>
  );
};
